import { useState, useEffect, useMemo, useRef, useCallback, memo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandGroup, CommandItem, CommandEmpty } from '@/components/ui/command';
import { Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { tasksAPI, usersAPI, prospectsAPI } from '@/lib/api';
import { INTERNAL_THEMES } from '@/lib/internalThemes';
import { cn } from '@/lib/utils';
import { FilRougeFrequencyFields, FIL_ROUGE_WEEKDAYS } from './FilRougeFrequencyFields';

// --- Rendering constants hoisted (recréation évitée par render) ---
const PRIORITY_OPTIONS = [
    { value: 'low', label: 'Basse' },
    { value: 'medium', label: 'Moyenne' },
    { value: 'high', label: 'Haute' },
    { value: 'urgent', label: 'Urgente' },
];

const MODULE_OPTIONS = [
    { value: 'both', label: 'Tout le dossier (Courtage + Travaux d\u2019optimisation)' },
    { value: 'easy_power', label: 'Courtage' },
    { value: 'chantier', label: 'Travaux d\u2019optimisation' },
];

const roleShort = (r) => {
    if (r === 'admin_full' || r === 'admin') return 'Admin';
    if (r === 'vp') return 'VP';
    if (r === 'apporteur') return 'Apporteur';
    if (r === 'comptable') return 'Comptable';
    return r || '';
};

/**
 * AssigneeList — extrait & mémoïsé pour éviter que la liste (souvent 100+
 * utilisateurs) ne re-render à CHAQUE frappe/checkbox du parent. Toute
 * l'interactivité passe par `onToggle(id, checked)` — pas de closure recréée.
 */
const AssigneeList = memo(function AssigneeList({ meId, assignableUsers, assigneeIds, onToggle }) {
    const [q, setQ] = useState('');
    const displayed = useMemo(() => {
        const others = assignableUsers.filter(u => u.id !== meId);
        const s = q.trim().toLowerCase();
        if (!s) return others.slice(0, 15);
        return others.filter(u =>
            (u.first_name?.toLowerCase().includes(s)) ||
            (u.last_name?.toLowerCase().includes(s)) ||
            (u.email?.toLowerCase().includes(s))
        ).slice(0, 25);
    }, [assignableUsers, meId, q]);
    const totalOthers = assignableUsers.length - (assignableUsers.some(u => u.id === meId) ? 1 : 0);
    const hasMore = !q && totalOthers > displayed.length;
    return (
        <div className="border border-slate-200 rounded-md bg-white">
            {totalOthers > 10 && (
                <div className="p-2 border-b border-slate-100">
                    <input
                        type="text"
                        placeholder="Filtrer par nom…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="w-full h-8 rounded-md border border-slate-200 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-slate-400"
                        data-testid="task-assignee-filter"
                    />
                </div>
            )}
            <div className="max-h-44 overflow-y-auto">
                <label className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer border-b border-slate-100">
                    <input
                        type="checkbox"
                        className="w-4 h-4 rounded text-blue-600"
                        checked={assigneeIds.includes(meId)}
                        onChange={(e) => onToggle(meId, e.target.checked)}
                        data-testid="task-assignee-me"
                    />
                    <span className="text-sm">Moi-même</span>
                </label>
                {displayed.map(u => (
                    <label key={u.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0">
                        <input
                            type="checkbox"
                            className="w-4 h-4 rounded text-blue-600"
                            checked={assigneeIds.includes(u.id)}
                            onChange={(e) => onToggle(u.id, e.target.checked)}
                            data-testid={`task-assignee-${u.id}`}
                        />
                        <span className="text-sm">{u.first_name} {u.last_name}</span>
                        <span className="text-xs text-slate-400">({roleShort(u.role)})</span>
                    </label>
                ))}
                {hasMore && (
                    <div className="px-3 py-2 text-[11px] text-slate-400 bg-slate-50">
                        … {totalOthers - displayed.length} autre(s) — tape un nom pour filtrer.
                    </div>
                )}
                {q && displayed.length === 0 && (
                    <div className="px-3 py-2 text-xs text-slate-500">Aucun utilisateur trouvé.</div>
                )}
            </div>
        </div>
    );
});

/**
 * NewTaskDialog — modal partagé de CRÉATION d'une tâche.
 *
 * Utilisé par :
 *   - `Tasks.jsx` (Actions > Tâches) : ouvert via bouton "Nouvelle tâche".
 *   - `QuickTaskEventCreator.jsx` (fiche dossier) : ouvert via bouton "Nouvelle tâche".
 *
 * Comportement unifié : même UI, mêmes champs (fil rouge inclus), même endpoint
 * (`tasksAPI.create` ou `tasksAPI.createFilRouge`). Statuts affichés côté listing :
 * `à faire` / `terminé` uniquement.
 *
 * Props :
 *   - open, onOpenChange : contrôle du Dialog
 *   - defaultProspect  : `{id, label|company_name|first_name+last_name}` pré-rempli
 *   - defaultCategory  : 'dossier' | 'interne' (défaut : 'dossier')
 *   - lockCategory     : bool — si true, verrouille la nature (utilisé depuis un dossier)
 *   - defaultModule    : 'both' | 'easy_power' | 'chantier'
 *   - onCreated        : callback après création (reçoit la tâche ou la liste fil-rouge)
 */
export function NewTaskDialog({
    open,
    onOpenChange,
    defaultProspect = null,
    defaultCategory = 'dossier',
    lockCategory = false,
    defaultModule = 'both',
    onCreated,
}) {
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin' || user?.role === 'admin_full';
    const isVP = user?.role === 'vp';
    const isApporteur = user?.role === 'apporteur';

    const [users, setUsers] = useState([]);
    const [teamMembers, setTeamMembers] = useState([]);
    const [prospects, setProspects] = useState([]);
    const [prospectsLoaded, setProspectsLoaded] = useState(false);
    const [prospectPopoverOpen, setProspectPopoverOpen] = useState(false);
    const [prospectSearch, setProspectSearch] = useState('');
    const [saving, setSaving] = useState(false);
    const [formKey, setFormKey] = useState(0);

    const [form, setForm] = useState({
        priority: 'medium',
        assigned_to: '', assignee_ids: [],
        prospect_id: defaultProspect?.id || '',
        module: defaultModule,
        category: defaultCategory,
        theme: '',
        is_team_task: false,
        is_fil_rouge: false,
        fil_rouge_frequency: 'daily', fil_rouge_weekday: 0,
        target_scope: 'custom',
    });

    const titleRef = useRef(null);
    const descriptionRef = useRef(null);
    // Refs fil rouge : inputs non-contrôlés (même pattern que titre/description)
    // pour éviter tout re-render à la saisie de la date/durée/heure.
    const filRougeStartRef = useRef(null);
    const filRougeDaysRef = useRef(null);
    const filRougeTimeRef = useRef(null);
    const dueDateRef = useRef(null);

    // Refs stabilisées : le parent peut re-render et re-passer un nouvel objet
    // `defaultProspect` à chaque frame (référence différente = tempête d'effets
    // et re-fetch en boucle). On stabilise via ID string + ref pour éviter les
    // triggers artificiels des effects sur ouverture.
    const defaultProspectId = defaultProspect?.id || '';
    const defaultProspectRef = useRef(defaultProspect);
    useEffect(() => { defaultProspectRef.current = defaultProspect; }, [defaultProspect]);

    // Reset état à chaque (ré)ouverture pour que la modale reparte propre.
    useEffect(() => {
        if (!open) return;
        setForm({
            priority: 'medium',
            assigned_to: '', assignee_ids: [],
            prospect_id: defaultProspectId,
            module: defaultModule,
            category: defaultCategory,
            theme: '',
            is_team_task: false,
            is_fil_rouge: false,
            fil_rouge_frequency: 'daily', fil_rouge_weekday: 0,
            target_scope: 'custom',
        });
        setProspectSearch('');
        setFormKey(k => k + 1);
    }, [open, defaultProspectId, defaultCategory, defaultModule]);

    // Chargement paresseux des dépendances (users, prospects) à l'ouverture.
    // Ne se déclenche qu'à `open` — évite les refetch parasites au retapage.
    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const _defaultProspect = defaultProspectRef.current;
                const [uRes, pRes, vpRes] = await Promise.all([
                    usersAPI.getPicker().catch(() => ({ data: [] })),
                    lockCategory && _defaultProspect
                        ? Promise.resolve({ data: [_defaultProspect] })
                        : prospectsAPI.getPicker().catch(() => ({ data: [] })),
                    isVP ? usersAPI.getVPTeam(user.id).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
                ]);
                setUsers(uRes.data || []);
                setProspects(pRes.data || []);
                setTeamMembers(vpRes.data || []);
                setProspectsLoaded(true);
            } catch (e) {
                // Silencieux — les listes vides sont gérées côté UI.
            }
        })();
    }, [open, isVP, user?.id, lockCategory]);

    const assignableUsers = useMemo(() => {
        if (isAdmin) return users.filter(u => u.is_active !== false);
        if (isVP) {
            const list = [user];
            users.forEach(u => {
                if (u.id === user.id || u.is_active === false) return;
                if (u.role === 'admin_full' || u.role === 'admin') { list.push(u); return; }
                if (u.vp_id === user.id) { list.push(u); return; }
            });
            teamMembers.forEach(m => { if (!list.find(a => a.id === m.id)) list.push(m); });
            return list;
        }
        if (isApporteur) {
            const list = [user];
            users.forEach(u => {
                if (u.id === user.id || u.is_active === false) return;
                if (u.role === 'admin_full' || u.role === 'admin') { list.push(u); return; }
                if (u.id === user.vp_id) { list.push(u); return; }
                if (u.role === 'apporteur' && u.vp_id === user.vp_id && user.vp_id) { list.push(u); return; }
            });
            return list;
        }
        return [];
    }, [users, user, isAdmin, isVP, isApporteur, teamMembers]);

    const filteredProspects = useMemo(() => {
        if (!prospectSearch || prospectSearch.trim().length < 1) return [];
        const s = prospectSearch.toLowerCase();
        return prospects.filter(p =>
            (p.company_name?.toLowerCase().includes(s)) ||
            (p.first_name?.toLowerCase().includes(s)) ||
            (p.last_name?.toLowerCase().includes(s)) ||
            (p.label?.toLowerCase().includes(s))
        ).slice(0, 25);
    }, [prospects, prospectSearch]);

    const selectedProspect = useMemo(() => {
        if (!form.prospect_id) return null;
        return prospects.find(p => p.id === form.prospect_id) || (defaultProspect?.id === form.prospect_id ? defaultProspect : null);
    }, [form.prospect_id, prospects, defaultProspect]);

    const displayProspect = (p) => p.label || p.company_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || '—';

    const setField = useCallback((patch) => setForm(f => ({ ...f, ...patch })), []);

    // Handler stable pour la liste (évite les rerenders de AssigneeList à chaque frappe).
    const toggleAssignee = useCallback((uid, checked) => {
        setForm(f => ({
            ...f,
            assignee_ids: checked
                ? Array.from(new Set([...(f.assignee_ids || []), uid]))
                : (f.assignee_ids || []).filter(id => id !== uid),
        }));
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const title = (titleRef.current?.value || '').trim();
        const description = descriptionRef.current?.value || '';
        const dueDate = dueDateRef.current?.value || '';
        const filRougeStart = filRougeStartRef.current?.value || '';
        const filRougeDaysRaw = filRougeDaysRef.current?.value || '';
        const filRougeTime = filRougeTimeRef.current?.value || '09:00';
        if (!title) { toast.error('Le titre est obligatoire'); return; }
        if (form.category === 'dossier') {
            if (!form.prospect_id) { toast.error('Une tâche « dossier » doit être liée à un prospect'); return; }
            if (!form.module) { toast.error('Choisissez le module (Courtage ou Travaux d’optimisation)'); return; }
        } else if (!form.theme) {
            toast.error('Choisissez un thème pour la tâche interne');
            return;
        }

        // Fil rouge (Admin/VP uniquement) — selon la fréquence choisie.
        if (form.is_fil_rouge) {
            if (!filRougeStart) { toast.error('Choisissez une date de début pour le fil rouge'); return; }
            const isWeekly = form.fil_rouge_frequency === 'weekly';
            const duration = Number(filRougeDaysRaw);
            if (isWeekly) {
                if (!Number.isInteger(duration) || duration < 1 || duration > 8) { toast.error('La durée doit être entre 1 et 8 semaines'); return; }
            } else if (!Number.isInteger(duration) || duration < 1 || duration > 60) { toast.error('Le nombre de jours doit être entre 1 et 60'); return; }
            // Hebdomadaire : la durée est saisie en semaines → convertie en jours pour le calendrier (1 relance par semaine, le jour choisi)
            const days = isWeekly ? duration * 7 : duration;
            if (form.target_scope === 'custom' && (form.assignee_ids || []).length === 0) {
                toast.error('Sélectionnez au moins une personne (ou choisissez « Tout le monde » / « Mon équipe »)'); return;
            }
            setSaving(true);
            try {
                const res = await tasksAPI.createFilRouge({
                    title, description, priority: form.priority,
                    start_date: filRougeStart, days, daily_time: filRougeTime,
                    frequency: form.fil_rouge_frequency,
                    weekday: form.fil_rouge_frequency === 'weekly' ? form.fil_rouge_weekday : null,
                    category: form.category,
                    prospect_id: form.category === 'dossier' ? form.prospect_id : null,
                    module: form.category === 'dossier' ? form.module : null,
                    theme: form.category === 'interne' ? form.theme : null,
                    target_scope: form.target_scope,
                    assignee_ids: form.target_scope === 'custom' ? form.assignee_ids : [],
                });
                toast.success(`Fil rouge créé — ${res.data.occurrences} occurrence(s) pour ${res.data.people} personne(s)`, { testId: 'fil-rouge-created-toast' });
                onCreated?.(res.data);
                onOpenChange?.(false);
            } catch (err) {
                const detail = err.response?.data?.detail;
                toast.error(typeof detail === 'string' ? detail : (detail?.[0]?.msg || 'Erreur création fil rouge'), { testId: 'fil-rouge-error-toast' });
            } finally {
                setSaving(false);
            }
            return;
        }

        // Tâche classique — mono-occurrence.
        setSaving(true);
        try {
            const assigneeIds = [...(form.assignee_ids || [])];
            const res = await tasksAPI.create({
                title,
                description,
                priority: form.priority,
                due_date: dueDate || null,
                category: form.category,
                prospect_id: form.category === 'dossier' ? form.prospect_id : null,
                module: form.category === 'dossier' ? form.module : null,
                theme: form.category === 'interne' ? form.theme : null,
                assigned_to: assigneeIds[0] || null,
                assignee_ids: assigneeIds,
                is_team_task: form.is_team_task,
            });
            toast.success('Tâche créée');
            onCreated?.(res.data);
            onOpenChange?.(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur création tâche');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[calc(100vw-2rem)] max-w-lg grid-cols-[minmax(0,1fr)] p-4 sm:p-6 max-h-[90vh] overflow-y-auto overflow-x-hidden sm:w-full" key={formKey} data-testid="new-task-dialog">
                <DialogHeader>
                    <DialogTitle data-testid="new-task-dialog-title">Nouvelle tâche</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="min-w-0 space-y-4">
                    <div className="space-y-2">
                        <Label>Titre *</Label>
                        <Input ref={titleRef} defaultValue="" placeholder="Ex: Appeler le prospect Dupont" required data-testid="task-title-input" />
                    </div>

                    {/* Nature — verrouillée si ouverte depuis un dossier */}
                    {!lockCategory && (
                        <div className="space-y-2">
                            <Label>Nature</Label>
                            <div className="grid grid-cols-2 gap-2">
                                <button type="button" onClick={() => setField({ category: 'dossier' })}
                                    data-testid="task-category-dossier"
                                    className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${form.category === 'dossier' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                                    🏢 Dossier
                                </button>
                                <button type="button" onClick={() => setField({ category: 'interne' })}
                                    data-testid="task-category-interne"
                                    className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${form.category === 'interne' ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                                    🗂️ Interne
                                </button>
                            </div>
                        </div>
                    )}

                    {form.category === 'dossier' ? (
                        <>
                            <div className="space-y-2">
                                <Label>Prospect lié *</Label>
                                {lockCategory && defaultProspect ? (
                                    <div className="min-h-9 break-words rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700" data-testid="task-prospect-locked">
                                        {displayProspect(defaultProspect)}
                                    </div>
                                ) : (
                                    <Popover open={prospectPopoverOpen} onOpenChange={setProspectPopoverOpen}>
                                        <PopoverTrigger asChild>
                                            <button
                                                type="button"
                                                data-testid="task-prospect-trigger"
                                                className="flex h-9 w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-left focus:outline-none focus:ring-2 focus:ring-slate-400"
                                            >
                                                {selectedProspect ? (
                                                    <span className="truncate">{displayProspect(selectedProspect)}</span>
                                                ) : (
                                                    <span className="text-slate-400">{prospectsLoaded ? 'Rechercher un dossier…' : 'Chargement…'}</span>
                                                )}
                                            </button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-[340px] max-w-[calc(100vw-2rem)] p-0" align="start" data-testid="task-prospect-options">
                                            <Command shouldFilter={false}>
                                                <CommandInput
                                                    autoFocus
                                                    placeholder="Tapez le nom du dossier…"
                                                    value={prospectSearch}
                                                    onValueChange={setProspectSearch}
                                                    data-testid="task-prospect-search"
                                                />
                                                <CommandList>
                                                    {prospectSearch.trim().length < 1 ? (
                                                        <div className="px-3 py-3 text-xs text-slate-500">
                                                            Tape au moins 1 caractère pour rechercher un dossier parmi ceux auxquels tu as accès.
                                                        </div>
                                                    ) : filteredProspects.length === 0 ? (
                                                        <CommandEmpty>Aucun dossier trouvé pour « {prospectSearch} »</CommandEmpty>
                                                    ) : (
                                                        <CommandGroup>
                                                            {filteredProspects.map(p => (
                                                                <CommandItem
                                                                    key={p.id}
                                                                    onSelect={() => {
                                                                        setField({ prospect_id: p.id });
                                                                        setProspectPopoverOpen(false);
                                                                        setProspectSearch('');
                                                                    }}
                                                                >
                                                                    <Check className={cn('mr-2 h-4 w-4', form.prospect_id === p.id ? 'opacity-100' : 'opacity-0')} />
                                                                    {displayProspect(p)}
                                                                </CommandItem>
                                                            ))}
                                                        </CommandGroup>
                                                    )}
                                                </CommandList>
                                            </Command>
                                        </PopoverContent>
                                    </Popover>
                                )}
                                <p className="text-xs text-slate-400">Une tâche « dossier » est rattachée à un prospect.</p>
                            </div>
                            <div className="space-y-2">
                                <Label>Mur du dossier concerné *</Label>
                                <Select value={form.module} onValueChange={(v) => setField({ module: v })}>
                                    <SelectTrigger className="h-auto min-h-9 whitespace-normal text-left [&>span]:line-clamp-none [&>svg]:shrink-0" data-testid="task-module-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="both">Tout le dossier (Courtage + Travaux d’optimisation)</SelectItem>
                                        <SelectItem value="easy_power">Courtage</SelectItem>
                                        <SelectItem value="chantier">Travaux d’optimisation</SelectItem>
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-slate-400">La tâche et ses changements de statut apparaîtront sur ce mur.</p>
                            </div>
                        </>
                    ) : (
                        <div className="space-y-2">
                            <Label>Thème *</Label>
                            <Select value={form.theme || ''} onValueChange={(v) => setField({ theme: v })}>
                                <SelectTrigger className="h-auto min-h-9 whitespace-normal text-left [&>span]:line-clamp-none [&>svg]:shrink-0" data-testid="task-theme-select"><SelectValue placeholder="Choisir un thème" /></SelectTrigger>
                                <SelectContent>
                                    {INTERNAL_THEMES.map(t => (
                                        <SelectItem key={t} value={t}>{t}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-slate-400">Tâche organisationnelle, non liée à un dossier.</p>
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label>Description</Label>
                        <Textarea ref={descriptionRef} defaultValue="" placeholder="Détails supplémentaires..." rows={2} data-testid="task-description-input" />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Priorité</Label>
                            <Select value={form.priority} onValueChange={(v) => setField({ priority: v })}>
                                <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="low">Basse</SelectItem>
                                    <SelectItem value="medium">Moyenne</SelectItem>
                                    <SelectItem value="high">Haute</SelectItem>
                                    <SelectItem value="urgent">Urgente</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Date limite</Label>
                            <Input type="date" ref={dueDateRef} defaultValue="" disabled={form.is_fil_rouge} data-testid="task-due-date" />
                        </div>
                    </div>

                    {/* Fil rouge — Admin/VP uniquement */}
                    {(isAdmin || isVP) && (
                        <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-3 space-y-3">
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input type="checkbox" checked={form.is_fil_rouge}
                                    onChange={(e) => setField({ is_fil_rouge: e.target.checked })}
                                    className="w-4 h-4 rounded text-rose-600" data-testid="task-fil-rouge-toggle" />
                                <span className="text-sm">
                                    <span className="font-semibold text-rose-700">🧵 Tâche fil rouge</span>
                                </span>
                            </label>
                            {form.is_fil_rouge && (
                                <>
                                    <FilRougeFrequencyFields
                                        frequency={form.fil_rouge_frequency}
                                        weekday={form.fil_rouge_weekday}
                                        onFrequencyChange={(value) => setField({ fil_rouge_frequency: value })}
                                        onWeekdayChange={(value) => setField({ fil_rouge_weekday: value })}
                                    />
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                        <div className="min-w-0 space-y-1">
                                            <Label htmlFor="fil-rouge-start-date" className="text-xs">Début</Label>
                                            <Input id="fil-rouge-start-date" type="date" required ref={filRougeStartRef} defaultValue="" data-testid="fil-rouge-start" />
                                        </div>
                                        <div className="min-w-0 space-y-1">
                                            <Label htmlFor="fil-rouge-duration" className="text-xs">
                                                {form.fil_rouge_frequency === 'weekly' ? 'Durée (semaines)' : 'Durée (jours)'}
                                            </Label>
                                            <Input
                                                key={form.fil_rouge_frequency}
                                                id="fil-rouge-duration" type="number" min={1}
                                                max={form.fil_rouge_frequency === 'weekly' ? 8 : 60} step={1} required
                                                ref={filRougeDaysRef}
                                                defaultValue={form.fil_rouge_frequency === 'weekly' ? '4' : '7'}
                                                data-testid="fil-rouge-days"
                                            />
                                        </div>
                                        <div className="min-w-0 space-y-1">
                                            <Label htmlFor="fil-rouge-due-time" className="text-xs">Échéance (Paris)</Label>
                                            <Input id="fil-rouge-due-time" type="time" required ref={filRougeTimeRef} defaultValue="09:00" data-testid="fil-rouge-time" />
                                        </div>
                                    </div>
                                    <p className="text-[11px] text-rose-700/80" data-testid="fil-rouge-rhythm-hint">
                                        {form.fil_rouge_frequency === 'weekly'
                                            ? `1 relance par semaine, chaque ${FIL_ROUGE_WEEKDAYS[form.fil_rouge_weekday].toLowerCase()}, pendant le nombre de semaines indiqué.`
                                            : '1 relance par jour pendant le nombre de jours indiqué.'}
                                    </p>
                                    <div className="space-y-1">
                                        <Label className="text-xs">Pour qui ?</Label>
                                        <Select value={form.target_scope} onValueChange={(v) => setField({ target_scope: v })}>
                                            <SelectTrigger className="h-auto min-h-9 whitespace-normal text-left [&>span]:line-clamp-none [&>svg]:shrink-0" data-testid="fil-rouge-scope"><SelectValue /></SelectTrigger>
                                            <SelectContent data-testid="fil-rouge-scope-options">
                                                {isAdmin && <SelectItem value="everyone" data-testid="fil-rouge-scope-everyone">Tout le monde</SelectItem>}
                                                {isVP && <SelectItem value="team" data-testid="fil-rouge-scope-team">Mon équipe (mes apporteurs)</SelectItem>}
                                                <SelectItem value="custom" data-testid="fil-rouge-scope-custom">Personnes choisies ci-dessous</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {(!form.is_fil_rouge || form.target_scope === 'custom') && (
                        <div className="space-y-2">
                            <Label>Assigner à <span className="text-xs text-slate-400">(plusieurs possibles)</span></Label>
                            <AssigneeList
                                meId={user?.id}
                                assignableUsers={assignableUsers}
                                assigneeIds={form.assignee_ids}
                                onToggle={toggleAssignee}
                            />
                            {form.assignee_ids.length === 0 && (
                                <p className="text-xs text-slate-500">Si personne n'est sélectionné, la tâche vous sera assignée.</p>
                            )}
                        </div>
                    )}

                    {isVP && !form.is_fil_rouge && (
                        <div className="flex items-center gap-3 p-3 bg-violet-50 rounded-lg">
                            <input type="checkbox" id="is_team_task" checked={form.is_team_task}
                                onChange={(e) => setField({ is_team_task: e.target.checked })}
                                className="w-4 h-4 rounded text-violet-600" />
                            <label htmlFor="is_team_task" className="text-sm">
                                <span className="font-medium text-violet-700">Tâche d'équipe</span>
                                <span className="text-violet-500 ml-1">(visible par tous les membres)</span>
                            </label>
                        </div>
                    )}

                    <Button type="submit" className="w-full" disabled={saving} data-testid="save-task-btn">
                        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {form.is_fil_rouge ? 'Créer le fil rouge' : 'Créer la tâche'}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default NewTaskDialog;
